'use client'

import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import type React from 'react'
import { Button } from './button'

export function FlyoutMenu(props: Headless.PopoverProps) {
  return <Headless.Popover {...props} />
}

export function FlyoutButton<T extends React.ElementType = typeof Button>({
  // @ts-ignore
  as = Button,
  ...props
}: { className?: string } & Omit<Headless.PopoverButtonProps<T>, 'className'>) {
  // @ts-ignore
  return <Headless.PopoverButton as={as} {...props} />
}

export function FlyoutPanel({
  className,
  viewport = false,
  viewportTop = 64,
  style,
  ...props
}: {
  className?: string
  viewport?: boolean
  viewportTop?: number
} & Omit<Headless.PopoverPanelProps, 'as' | 'className'>) {
  return (
    <Headless.PopoverPanel
      {...props}
      portal={viewport ? true : props.portal}
      transition
      style={viewport ? {...style, top: viewportTop} : style}
      className={clsx(
        className,
        viewport ? 'fixed inset-x-0 z-50 isolate' : 'absolute inset-x-0 top-full z-50 isolate',
        'border-y border-zinc-950/10 bg-white/75 backdrop-blur-xl dark:border-white/10 dark:bg-zinc-800/75',
        'shadow-[0_18px_18px_-18px_rgba(15,23,42,0.75),0_6px_0_-4px_rgba(15,23,42,0.16)] ring-1 ring-zinc-950/10 dark:shadow-[0_18px_18px_-18px_rgba(0,0,0,0.95),0_6px_0_-4px_rgba(0,0,0,0.45)] dark:ring-white/10 dark:ring-inset',
        'transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 data-closed:data-enter:-translate-y-1 data-closed:data-enter:opacity-0 data-enter:duration-150'
      )}
    />
  )
}

export function FlyoutHeading({ className, ...props }: React.ComponentPropsWithoutRef<'h3'>) {
  return (
    <h3
      {...props}
      className={clsx(
        className,
        'px-2 text-xs/5 font-medium text-zinc-500 dark:text-zinc-400'
      )}
    />
  )
}

export function FlyoutItem({
  className,
  ...props
}: { className?: string } & Omit<Headless.ButtonProps, 'as' | 'className'>) {
  return (
    <Headless.Button
      {...props}
      type="button"
      className={clsx(
        className,
        'group flex w-full cursor-default items-start gap-3 rounded-lg px-3 py-2.5 text-left sm:px-2 sm:py-2',
        'text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]',
        'data-hover:bg-zinc-950/5 data-focus:bg-blue-500 data-focus:text-white dark:data-hover:bg-white/5',
        'forced-color-adjust-none forced-colors:data-focus:bg-[Highlight] forced-colors:data-focus:text-[HighlightText]',
        '*:data-[slot=icon]:mt-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:text-zinc-500',
        'data-focus:*:data-[slot=icon]:text-white dark:*:data-[slot=icon]:text-zinc-400 dark:data-focus:*:data-[slot=icon]:text-white'
      )}
    />
  )
}

export function FlyoutLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={clsx(className, 'block font-medium')} />
}

export function FlyoutDescription({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'mt-0.5 block text-sm/5 text-zinc-500 group-data-focus:text-white sm:text-xs/5 dark:text-zinc-400 forced-colors:group-data-focus:text-[HighlightText]'
      )}
    />
  )
}
